import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { driver, type Config, type Driver, type DriveStep } from 'driver.js';
import { AuthService } from './auth.service';
import { LoanSelectionService } from './loan-selection.service';
import { PlatformService } from './platform.service';
import type { OriginationApplicationModel } from '@ld.consumer-portal/models';
import { buildTourSteps, TOUR_STEPS, TourStep } from './tour-steps.config';
import { UserIdleService } from './user-idle.service';

export interface TourState {
  isActive: boolean;
  currentStepIndex: number;
  tourSteps: TourStep[];
}

@Injectable({
  providedIn: 'root',
})
export class TourGuideService {
  private readonly router = inject(Router);
  private readonly loanSelectionService = inject(LoanSelectionService);
  private readonly transloco = inject(TranslocoService);
  private readonly authService = inject(AuthService);
  private readonly userIdleService = inject(UserIdleService);
  private readonly platformService = inject(PlatformService);
  private readonly breakpointObserver = inject(BreakpointObserver);

  /** Matches the mobile layout breakpoint used by the dashboard/loan layouts. */
  private static readonly MOBILE_VIEWPORT_QUERY = '(max-width: 767px)';

  private driverInstance: Driver | null = null;
  private isDestroying = false;

  private breakpointSubscription: Subscription | null = null;
  private isRebuilding = false;
  private lastIsMobile = false;
  private tourContextApps: OriginationApplicationModel[] = [];
  private tourContextHasLinkableLoan = false;

  private readonly _isActive = signal(false);
  private readonly _currentStepIndex = signal(0);
  private readonly _tourSteps = signal<TourStep[]>(TOUR_STEPS);
  private readonly _showCompletionModal = signal(false);
  private readonly _originationLoanId = signal<string | null>(null);

  readonly isActive = this._isActive.asReadonly();
  readonly currentStepIndex = this._currentStepIndex.asReadonly();
  readonly tourSteps = this._tourSteps.asReadonly();
  readonly showCompletionModal = this._showCompletionModal.asReadonly();

  readonly currentStep = computed<TourStep | null>(() => {
    const steps = this._tourSteps();
    const index = this._currentStepIndex();
    return steps[index] ?? null;
  });

  readonly totalSteps = computed(() => this._tourSteps().length);

  readonly isFirstStep = computed(() => this._currentStepIndex() === 0);

  readonly isLastStep = computed(() => {
    const steps = this._tourSteps();
    return this._currentStepIndex() === steps.length - 1;
  });

  readonly progress = computed(() => {
    const total = this._tourSteps().length;
    if (total === 0) return 0;
    return ((this._currentStepIndex() + 1) / total) * 100;
  });

  private onTourComplete: (() => void) | null = null;
  private onTourSkip: (() => void) | null = null;

  private readonly authLostEffect = effect(() => {
    const authenticated = this.authService.isAuthenticated();
    if (!authenticated && this._isActive()) {
      untracked(() => this.forceStop());
    }
  });

  private readonly idleWarningEffect = effect(() => {
    const warning = this.userIdleService.showWarning();
    if (warning && this._isActive()) {
      untracked(() => this.forceStop());
    }
  });

  setCallbacks(onComplete: () => void, onSkip: () => void): void {
    this.onTourComplete = onComplete;
    this.onTourSkip = onSkip;
  }

  setTourSteps(steps: TourStep[]): void {
    this._tourSteps.set(steps);
  }

  setOriginationLoanId(loanId: string | null): void {
    this._originationLoanId.set(loanId);
  }

  prepareTourSteps(originationApps: OriginationApplicationModel[], hasLinkableLoan = false): boolean {
    const isMobile = this.isMobileLayout();
    const steps = this.buildStepsForContext(originationApps, hasLinkableLoan, isMobile);

    if (steps.length === 0) return false;

    this.tourContextApps = originationApps;
    this.tourContextHasLinkableLoan = hasLinkableLoan;
    this.lastIsMobile = isMobile;
    this._tourSteps.set(steps);

    const firstPendingApp = originationApps.find(
      (app) => app.status === 'PendingApproval' && (app.loanType === 'Purchase' || app.loanType === 'Refinance'),
    );
    if (firstPendingApp) {
      this._originationLoanId.set(firstPendingApp.id ?? null);
    }

    return true;
  }

  private buildStepsForContext(
    originationApps: OriginationApplicationModel[],
    hasLinkableLoan: boolean,
    isMobile: boolean,
  ): TourStep[] {
    const hasServicingLoans = this.loanSelectionService.hasMortgageOrHeloc();
    const hasPendingOrigination = originationApps.some(
      (app) => app.status === 'PendingApproval' && (app.loanType === 'Purchase' || app.loanType === 'Refinance'),
    );
    const hasPastApplications = originationApps.some(
      (app) => app.status !== 'Unsubmitted' && app.status !== 'PendingApproval',
    );
    return buildTourSteps(hasPendingOrigination, hasServicingLoans, isMobile, hasPastApplications, hasLinkableLoan);
  }

  /**
   * Mirrors the layout components: the mobile tour steps are used both in the
   * native app and whenever the responsive layout renders the mobile UI
   * (viewport <= 767px), so highlights target the elements that are actually
   * on screen.
   */
  private isMobileLayout(): boolean {
    return (
      this.platformService.isNativeMobile() ||
      this.breakpointObserver.isMatched(TourGuideService.MOBILE_VIEWPORT_QUERY)
    );
  }

  forceStop(): void {
    if (!this._isActive()) return;
    this.isDestroying = true;
    this.stopBreakpointWatch();
    this.closeMenuExpansion();
    this.onTourSkip?.();
    this.onTourComplete = null;
    this.onTourSkip = null;
    this.destroyDriver();
    this._isActive.set(false);
    this._currentStepIndex.set(0);
    this._showCompletionModal.set(false);
    this._tourSteps.set(TOUR_STEPS);
    this._originationLoanId.set(null);
    this.isDestroying = false;
  }

  async startTour(startIndex = 0): Promise<void> {
    const steps = this._tourSteps();
    if (steps.length === 0) return;

    const validIndex = Math.max(0, Math.min(startIndex, steps.length - 1));
    this._currentStepIndex.set(validIndex);
    this._isActive.set(true);

    const firstStep = steps[validIndex];
    if (firstStep?.route) {
      await this.navigateToStepRoute(firstStep);
    }

    await this.waitForElement(firstStep);
    await this.waitForLayout();
    this.initDriver(steps, validIndex);
    this.watchBreakpointChanges();
  }

  /**
   * While the tour is active, watch for the responsive-layout breakpoint being
   * crossed (e.g. a browser window resized across 767px). When the layout flips
   * between the mobile and desktop UIs the previously built step set targets
   * elements that are no longer rendered, so we rebuild the steps for the new
   * layout and re-render the driver on the equivalent step.
   */
  private watchBreakpointChanges(): void {
    this.stopBreakpointWatch();
    this.breakpointSubscription = this.breakpointObserver
      .observe(TourGuideService.MOBILE_VIEWPORT_QUERY)
      .subscribe(() => {
        void this.rebuildForLayoutChange();
      });
  }

  private stopBreakpointWatch(): void {
    this.breakpointSubscription?.unsubscribe();
    this.breakpointSubscription = null;
  }

  private async rebuildForLayoutChange(): Promise<void> {
    if (!this._isActive() || this.isDestroying || this.isRebuilding) return;

    const isMobile = this.isMobileLayout();
    if (isMobile === this.lastIsMobile) return;

    this.isRebuilding = true;
    try {
      const currentStepId = this.currentStep()?.id;
      const steps = this.buildStepsForContext(this.tourContextApps, this.tourContextHasLinkableLoan, isMobile);
      this.lastIsMobile = isMobile;

      if (steps.length === 0) return;

      const mappedIndex = currentStepId ? steps.findIndex((step) => step.id === currentStepId) : -1;
      const newIndex = mappedIndex >= 0 ? mappedIndex : Math.min(this._currentStepIndex(), steps.length - 1);

      this._tourSteps.set(steps);
      this._currentStepIndex.set(newIndex);
      await this.refreshActiveStep(newIndex, steps);
    } finally {
      this.isRebuilding = false;
    }
  }

  private async refreshActiveStep(index: number, steps: TourStep[]): Promise<void> {
    const step = steps[index];
    if (!step) return;

    this.closeMenuExpansion();

    if (step.route) {
      await this.navigateToStepRoute(step);
    }

    await this.waitForElement(step);
    await this.waitForLayout();

    if (step.beforeAction) {
      await this.executeBeforeAction(step);
    }

    this.initDriver(steps, index);
  }

  async nextStep(): Promise<void> {
    if (!this._isActive()) return;

    const steps = this._tourSteps();
    const currentIndex = this._currentStepIndex();

    if (currentIndex >= steps.length - 1) {
      this.endTour();
      return;
    }

    const nextIndex = currentIndex + 1;
    this._currentStepIndex.set(nextIndex);

    const nextStep = steps[nextIndex];
    if (nextStep?.route) {
      await this.navigateToStepRoute(nextStep);
    }
  }

  async previousStep(): Promise<void> {
    if (!this._isActive()) return;

    const currentIndex = this._currentStepIndex();
    if (currentIndex <= 0) return;

    const prevIndex = currentIndex - 1;
    this._currentStepIndex.set(prevIndex);

    const steps = this._tourSteps();
    const prevStep = steps[prevIndex];
    if (prevStep?.route) {
      await this.navigateToStepRoute(prevStep);
    }
  }

  async goToStep(index: number): Promise<void> {
    if (!this._isActive()) return;

    const steps = this._tourSteps();
    if (index < 0 || index >= steps.length) return;

    this._currentStepIndex.set(index);

    const step = steps[index];
    if (step?.route) {
      await this.navigateToStepRoute(step);
    }
  }

  endTour(): void {
    this.handleTourComplete();
  }

  skipTour(): void {
    this.handleTourSkip();
  }

  private initDriver(steps: TourStep[], startIndex: number): void {
    this.destroyDriver();

    const driveSteps = this.buildDriverSteps(steps);
    const endTourText = this.transloco.translate('tourEndTour');

    const config: Config = {
      showProgress: true,
      animate: true,
      smoothScroll: true,
      allowClose: false,
      disableActiveInteraction: true,
      stagePadding: 8,
      stageRadius: 8,
      popoverOffset: 16,
      overlayOpacity: 0.7,
      popoverClass: 'ld-tour-popover',
      showButtons: ['next', 'previous', 'close'],
      onPopoverRender: (popover) => {
        const stepIndex = this._currentStepIndex();

        // Move step counter into title area: [Title] ... [Step X of Y]
        if (popover.title) {
          const counterText = this.transloco.translate('tourStepCounter', {
            current: stepIndex + 1,
            total: steps.length,
          });
          const counterEl = document.createElement('span');
          counterEl.className = 'driver-popover-step-counter';
          counterEl.textContent = counterText;
          popover.title.style.display = 'flex';
          popover.title.style.justifyContent = 'space-between';
          popover.title.style.alignItems = 'center';
          popover.title.appendChild(counterEl);
        }

        // Restructure footer: [End Tour] ... [< Previous] [Next >]
        if (popover.footer) {
          if (popover.closeButton) {
            popover.closeButton.innerHTML = endTourText;
            popover.closeButton.classList.add('driver-popover-end-tour-btn');
            popover.footer.prepend(popover.closeButton);
          }

          // Set button text with carets and proper spacing to match Figma
          if (popover.previousButton) {
            popover.previousButton.innerHTML =
              '<span>&#10092;</span><span>' + this.transloco.translate('tourButtonPrevious').toUpperCase() + '</span>';
            popover.previousButton.classList.remove('driver-popover-prev-btn-loading');
            if (stepIndex === 0) {
              popover.previousButton.disabled = true;
              popover.previousButton.classList.add('driver-popover-prev-btn-disabled');
            } else {
              popover.previousButton.disabled = false;
              popover.previousButton.classList.remove('driver-popover-prev-btn-disabled');
            }
          }
          if (popover.nextButton) {
            popover.nextButton.innerHTML =
              '<span>' + this.transloco.translate('tourButtonNext').toUpperCase() + '</span><span>&#10093;</span>';
            popover.nextButton.disabled = false;
            popover.nextButton.classList.remove('driver-popover-next-btn-loading');
          }
        }

        // Hide the default progress text
        if (popover.progress) {
          popover.progress.style.display = 'none';
        }
      },
      steps: driveSteps,
      onNextClick: (
        _el: Element | undefined,
        _step: DriveStep,
        opts: { config: Config; state: { activeIndex?: number }; driver: Driver },
      ) => {
        const stepIndex = this._currentStepIndex();
        const nextIndex = stepIndex + 1;

        if (nextIndex >= steps.length) {
          this.handleTourComplete();
          return;
        }

        const currentTourStep = steps[stepIndex];
        this._currentStepIndex.set(nextIndex);
        const nextTourStep = steps[nextIndex];

        const needsAsyncHandling =
          (nextTourStep?.route && nextTourStep.route !== currentTourStep?.route) ||
          nextTourStep?.beforeAction ||
          currentTourStep?.beforeAction;

        if (needsAsyncHandling) {
          this.setButtonLoadingState('.driver-popover-next-btn', 'driver-popover-next-btn-loading', true);
          this.transitionToStep(nextTourStep, currentTourStep, nextIndex, steps, opts).catch(() => {
            this.setButtonLoadingState('.driver-popover-next-btn', 'driver-popover-next-btn-loading', false);
            this.handleTourSkip();
          });
        } else {
          this.driverInstance?.moveNext();
        }
      },
      onPrevClick: (
        _el: Element | undefined,
        _step: DriveStep,
        opts: { config: Config; state: { activeIndex?: number }; driver: Driver },
      ) => {
        const stepIndex = this._currentStepIndex();
        const prevIndex = stepIndex - 1;

        if (prevIndex < 0) return;

        const currentTourStep = steps[stepIndex];
        this._currentStepIndex.set(prevIndex);
        const prevTourStep = steps[prevIndex];

        const needsAsyncHandling =
          (prevTourStep?.route && prevTourStep.route !== currentTourStep?.route) ||
          prevTourStep?.beforeAction ||
          currentTourStep?.beforeAction;

        if (needsAsyncHandling) {
          this.setButtonLoadingState('.driver-popover-prev-btn', 'driver-popover-prev-btn-loading', true);
          this.transitionToStep(prevTourStep, currentTourStep, prevIndex, steps, opts).catch(() => {
            this.setButtonLoadingState('.driver-popover-prev-btn', 'driver-popover-prev-btn-loading', false);
            this.handleTourSkip();
          });
        } else {
          this.driverInstance?.movePrevious();
        }
      },
      onCloseClick: () => {
        this.handleTourSkip();
      },
      onDestroyStarted: () => {
        if (this.driverInstance?.isLastStep()) {
          this.handleTourComplete();
        } else {
          this.handleTourSkip();
        }
      },
    };

    this.driverInstance = driver(config);
    this.driverInstance.drive(startIndex);
  }

  private async transitionToStep(
    targetStep: TourStep,
    previousStep: TourStep | undefined,
    targetIndex: number,
    steps: TourStep[],
    opts: { config: Config },
  ): Promise<void> {
    if (previousStep?.beforeAction) {
      this.closeMenuExpansion();
      await this.delay(100);
    }

    if (targetStep?.route && targetStep.route !== previousStep?.route) {
      await this.navigateToStepRoute(targetStep);
      await this.waitForElement(targetStep);
    }

    if (targetStep?.beforeAction) {
      await this.executeBeforeAction(targetStep);
    }

    opts.config.steps = this.buildDriverSteps(steps);
    this.driverInstance?.moveTo(targetIndex);
  }

  private buildDriverSteps(steps: TourStep[]): DriveStep[] {
    return steps.map((step) => {
      const title = this.transloco.translate(step.titleKey);
      const description = this.transloco.translate(step.descriptionKey);

      const driveStep: DriveStep = {
        popover: {
          title,
          description,
          side: this.mapPosition(step.position),
          align: 'center',
        },
      };

      const resolvedElementId = this.resolveTargetElementId(step);
      if (resolvedElementId) {
        driveStep.element = `#${resolvedElementId}`;
      } else if (step.targetSelector) {
        const selectorEl = document.querySelector(step.targetSelector);
        if (selectorEl) {
          driveStep.element = step.targetSelector;
        }
      }

      return driveStep;
    });
  }

  private mapPosition(position: string): 'top' | 'bottom' | 'left' | 'right' | undefined {
    switch (position) {
      case 'top':
        return 'top';
      case 'bottom':
        return 'bottom';
      case 'left':
        return 'left';
      case 'right':
        return 'right';
      case 'center':
        return undefined;
      default:
        return 'bottom';
    }
  }

  private handleTourComplete(): void {
    if (this.isDestroying) return;
    this.isDestroying = true;
    this.stopBreakpointWatch();
    this.closeMenuExpansion();
    this.onTourComplete?.();
    this.destroyDriver();
    this._isActive.set(false);
    this._currentStepIndex.set(0);
    this._tourSteps.set(TOUR_STEPS);
    this._originationLoanId.set(null);
    this.isDestroying = false;
    this._showCompletionModal.set(true);
  }

  dismissCompletionModal(): void {
    this._showCompletionModal.set(false);
    if (!this.router.url.startsWith('/dashboard')) {
      this.router.navigateByUrl('/dashboard');
    }
  }

  private handleTourSkip(): void {
    if (this.isDestroying) return;
    this.isDestroying = true;
    this.stopBreakpointWatch();
    this.closeMenuExpansion();
    this.onTourSkip?.();
    this.destroyDriver();
    this._isActive.set(false);
    this._currentStepIndex.set(0);
    this._tourSteps.set(TOUR_STEPS);
    this._originationLoanId.set(null);
    this.isDestroying = false;
    if (!this.router.url.startsWith('/dashboard')) {
      this.router.navigateByUrl('/dashboard');
    }
  }

  private destroyDriver(): void {
    if (this.driverInstance) {
      this.driverInstance.destroy();
      this.driverInstance = null;
    }
  }

  private async navigateToStepRoute(step: TourStep): Promise<void> {
    if (!step.route) return;

    let route = step.route;

    if (route.includes(':accountId')) {
      const accountId = this.getFirstAccountId();
      if (accountId) {
        route = route.replace(':accountId', accountId);
      } else {
        return;
      }
    }

    if (route.includes(':loanId')) {
      const loanId = this._originationLoanId();
      if (loanId) {
        route = route.replace(':loanId', loanId);
      } else {
        return;
      }
    }

    await this.router.navigateByUrl(route);
  }

  private getFirstAccountId(): string | null {
    const availableLoans = this.loanSelectionService.availableLoans();
    if (availableLoans.length === 0) return null;

    // Prioritize mortgage accounts over HELOC
    const mortgageLoan = availableLoans.find(
      (loan) => this.loanSelectionService.getLoanTypeShortName(loan) === 'MORTGAGE',
    );
    if (mortgageLoan) return mortgageLoan.loanNumber ?? null;

    // Fall back to first available loan
    return availableLoans[0].loanNumber ?? null;
  }

  private resolveTargetElementId(step: TourStep): string | null {
    if (!step.targetElementId && !step.additionalTargetIds?.length) return null;

    if (step.targetElementId && this.isElementVisible(step.targetElementId)) {
      return step.targetElementId;
    }

    if (step.additionalTargetIds) {
      for (const id of step.additionalTargetIds) {
        if (this.isElementVisible(id)) return id;
      }
    }

    if (step.fallbackTargetElementId && this.isElementVisible(step.fallbackTargetElementId)) {
      return step.fallbackTargetElementId;
    }

    return step.targetElementId || null;
  }

  private isElementVisible(elementId: string): boolean {
    const el = document.getElementById(elementId);
    if (!el) return false;
    return el.offsetParent !== null || el.offsetWidth > 0 || el.offsetHeight > 0;
  }

  private waitForElement(step: TourStep): Promise<void> {
    if (!step.targetElementId && !step.additionalTargetIds?.length) return Promise.resolve();

    return new Promise((resolve) => {
      const maxAttempts = 20;
      let attempts = 0;

      const check = () => {
        attempts++;
        const found =
          (step.targetElementId && document.getElementById(step.targetElementId)) ||
          (step.fallbackTargetElementId && document.getElementById(step.fallbackTargetElementId)) ||
          step.additionalTargetIds?.some((id) => document.getElementById(id));
        if (found || attempts >= maxAttempts) {
          resolve();
        } else {
          setTimeout(check, 100);
        }
      };

      setTimeout(check, 50);
    });
  }

  private async executeBeforeAction(step: TourStep): Promise<void> {
    if (!step.beforeAction) return;

    let triggerElementId: string | undefined;

    switch (step.beforeAction) {
      case 'expand-settings-menu':
        triggerElementId = 'nav-tab-settings';
        this.clickElement(triggerElementId);
        break;
      case 'expand-help-menu':
        triggerElementId = 'help-resources-btn';
        this.clickElement(triggerElementId);
        break;
    }

    // Highlight the menu trigger button alongside the expanded dropdown
    if (triggerElementId) {
      const triggerEl = document.getElementById(triggerElementId);
      triggerEl?.classList.add('tour-highlighted-trigger');

      // Elevate the servicing header stacking context above the Driver.js overlay (z-index 10000)
      // so the tab button visually appears above the overlay
      const header = document.getElementById('servicing-header');
      if (header) {
        header.style.zIndex = '10001';
      }
    }

    document.body.classList.add('tour-menu-expanded');
    await this.waitForSelector('.mat-mdc-menu-panel');
  }

  private closeMenuExpansion(): void {
    // Remove highlight from any menu trigger buttons
    document.querySelectorAll('.tour-highlighted-trigger').forEach((el) => {
      el.classList.remove('tour-highlighted-trigger');
    });
    // Restore the servicing header z-index
    const header = document.getElementById('servicing-header');
    if (header) {
      header.style.zIndex = '';
    }
    // Re-enable pointer events on backdrop before clicking to close the menu
    document.body.classList.remove('tour-menu-expanded');
    const backdrop = document.querySelector('.cdk-overlay-backdrop') as HTMLElement;
    if (backdrop) {
      backdrop.click();
    }
  }

  private clickElement(elementId: string): void {
    const el = document.getElementById(elementId);
    el?.click();
  }

  private waitForSelector(selector: string | undefined): Promise<void> {
    if (!selector) return Promise.resolve();

    return new Promise((resolve) => {
      const maxAttempts = 20;
      let attempts = 0;

      const check = () => {
        attempts++;
        if (document.querySelector(selector) || attempts >= maxAttempts) {
          resolve();
        } else {
          setTimeout(check, 100);
        }
      };

      setTimeout(check, 50);
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private waitForLayout(): Promise<void> {
    return new Promise((resolve) => {
      requestAnimationFrame(() => setTimeout(resolve, 100));
    });
  }

  isTargetElementAvailable(elementId: string): boolean {
    return !!document.getElementById(elementId);
  }

  getTargetElementRect(elementId: string): DOMRect | null {
    const element = document.getElementById(elementId);
    return element?.getBoundingClientRect() ?? null;
  }

  private setButtonLoadingState(buttonSelector: string, loadingClass: string, isLoading: boolean): void {
    const btn = document.querySelector(`.ld-tour-popover ${buttonSelector}`) as HTMLButtonElement | null;
    if (!btn) return;

    if (isLoading) {
      btn.classList.add(loadingClass);
      btn.disabled = true;
      const spinnerClass = buttonSelector.includes('next') ? 'tour-spinner' : 'tour-spinner-dark';
      const loadingText = this.transloco.translate('tourButtonLoading').toUpperCase();
      btn.innerHTML = `<span class="${spinnerClass}"></span><span>${loadingText}</span>`;
    } else {
      btn.classList.remove(loadingClass);
      btn.disabled = false;
    }
  }
}
